import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Announcement as AnnouncementIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import BannerService from '../services/bannerService';
import config from '../config/config';
import { useAuth } from '../context/AuthContext';

const BANNER_TYPES = [
  { value: 'INFO', label: 'Info', color: '#2196f3' },
  { value: 'WARNING', label: 'Warning', color: '#ff9800' },
  { value: 'SUCCESS', label: 'Success', color: '#4caf50' },
];

const TARGET_PAGES = [
  { value: '', label: 'All Pages (Global)' },
  { value: '/dashboard', label: 'Dashboard' },
  { value: '/account', label: 'Account' },
  { value: '/settings', label: 'Settings' },
  { value: '/events', label: 'Events' },
  { value: '/tickets', label: 'Tickets' },
  { value: '/store', label: 'Store' },
  { value: '/library', label: 'Library' },
  { value: '/subscriptions', label: 'Subscriptions' },
  { value: '/notifications', label: 'Notifications' },
];

const EMPTY_FORM = {
  titleEn: '',
  titleEs: '',
  messageEn: '',
  messageEs: '',
  type: 'INFO',
  buttonTextEn: '',
  buttonTextEs: '',
  buttonLink: '',
  targetTeam: '',
  targetPage: '',
  startDate: '',
  endDate: '',
  isCloseable: true,
  isActive: true,
};

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16);
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const getStatusChip = (status) => {
  const map = {
    ACTIVE: { color: 'success', label: 'Active' },
    INACTIVE: { color: 'default', label: 'Inactive' },
    SCHEDULED: { color: 'info', label: 'Scheduled' },
    EXPIRED: { color: 'warning', label: 'Expired' },
  };
  const config = map[status] || map.INACTIVE;
  return <Chip size="small" color={config.color} label={config.label} />;
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'WARNING': return <WarningIcon sx={{ color: '#ff9800', fontSize: 18 }} />;
    case 'SUCCESS': return <SuccessIcon sx={{ color: '#4caf50', fontSize: 18 }} />;
    default: return <InfoIcon sx={{ color: '#2196f3', fontSize: 18 }} />;
  }
};

const BannerManagementPage = () => {
  const { getAccessToken } = useAuth();
  const [banners, setBanners] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await BannerService.getAll();
      if (response.status === 'success') {
        setBanners(response.data || []);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch banners', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTeams = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setTeams(result.data || []);
        }
      }
    } catch {
      // Teams are optional for the dropdown
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchBanners();
    fetchTeams();
  }, [fetchBanners, fetchTeams]);

  const handleOpenCreate = () => {
    setSelectedBanner(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = (banner) => {
    setSelectedBanner(banner);
    setForm({
      titleEn: banner.titleEn || '',
      titleEs: banner.titleEs || '',
      messageEn: banner.messageEn || '',
      messageEs: banner.messageEs || '',
      type: banner.type || 'INFO',
      buttonTextEn: banner.buttonTextEn || '',
      buttonTextEs: banner.buttonTextEs || '',
      buttonLink: banner.buttonLink || '',
      targetTeam: banner.targetTeam || '',
      targetPage: banner.targetPage || '',
      startDate: formatDateForInput(banner.startDate),
      endDate: formatDateForInput(banner.endDate),
      isCloseable: banner.closeable ?? true,
      isActive: banner.active ?? true,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBanner(null);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.titleEn || !form.messageEn || !form.startDate || !form.endDate) {
      setSnackbar({ open: true, message: 'Please fill in all required fields (Title EN, Message EN, Start Date, End Date)', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        targetTeam: form.targetTeam || null,
        targetPage: form.targetPage || null,
        buttonTextEn: form.buttonTextEn || null,
        buttonTextEs: form.buttonTextEs || null,
        buttonLink: form.buttonLink || null,
      };

      if (selectedBanner) {
        await BannerService.update(selectedBanner.id, payload);
        setSnackbar({ open: true, message: 'Banner updated successfully', severity: 'success' });
      } else {
        await BannerService.create(payload);
        setSnackbar({ open: true, message: 'Banner created successfully', severity: 'success' });
      }

      handleCloseDialog();
      fetchBanners();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to save banner', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (banner) => {
    try {
      const newActive = !banner.active;
      await BannerService.toggleActive(banner.id, newActive);
      setSnackbar({ open: true, message: `Banner ${newActive ? 'activated' : 'deactivated'}`, severity: 'success' });
      fetchBanners();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to toggle banner', severity: 'error' });
    }
  };

  const handleDeleteConfirm = (banner) => {
    setSelectedBanner(banner);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedBanner) return;
    try {
      await BannerService.delete(selectedBanner.id);
      setSnackbar({ open: true, message: 'Banner deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setSelectedBanner(null);
      fetchBanners();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete banner', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AnnouncementIcon sx={{ fontSize: 28, color: '#667eea' }} />
          <Typography variant="h5" fontWeight={600}>Banner Management</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Create Banner
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title (EN)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Target Team</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Target Page</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {banners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <AnnouncementIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1, display: 'block', mx: 'auto' }} />
                    No banners created yet. Click "Create Banner" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                banners.map((banner) => (
                  <TableRow
                    key={banner.id}
                    sx={{
                      opacity: banner.status === 'EXPIRED' ? 0.5 : 1,
                      '&:hover': { backgroundColor: '#f9f9f9' }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getTypeIcon(banner.type)}
                        <Typography variant="body2">{banner.type}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 250 }}>
                        {banner.titleEn}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {banner.targetTeam || 'All Teams'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {banner.targetPage || 'All Pages'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.8rem">
                        {formatDateDisplay(banner.startDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.8rem">
                        {formatDateDisplay(banner.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(banner.status)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEdit(banner)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={banner.active ? 'Deactivate' : 'Activate'}>
                          <IconButton size="small" onClick={() => handleToggle(banner)} color={banner.active ? 'success' : 'default'}>
                            {banner.active ? <ToggleOnIcon fontSize="small" /> : <ToggleOffIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteConfirm(banner)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100%', sm: '90vh' },
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
          {selectedBanner ? 'Edit Banner' : 'Create New Banner'}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>

            {/* Content Section */}
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1, mb: 1.5, display: 'block' }}>
                Content
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Title (English) *"
                  fullWidth
                  value={form.titleEn}
                  onChange={(e) => handleFormChange('titleEn', e.target.value)}
                />
                <TextField
                  label="Title (Spanish)"
                  fullWidth
                  value={form.titleEs}
                  onChange={(e) => handleFormChange('titleEs', e.target.value)}
                />
                <TextField
                  label="Message (English) *"
                  fullWidth
                  multiline
                  rows={3}
                  value={form.messageEn}
                  onChange={(e) => handleFormChange('messageEn', e.target.value)}
                />
                <TextField
                  label="Message (Spanish)"
                  fullWidth
                  multiline
                  rows={3}
                  value={form.messageEs}
                  onChange={(e) => handleFormChange('messageEs', e.target.value)}
                />
              </Box>
            </Box>

            {/* Appearance Section */}
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1, mb: 1.5, display: 'block' }}>
                Appearance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Banner Type</InputLabel>
                  <Select
                    value={form.type}
                    label="Banner Type"
                    onChange={(e) => handleFormChange('type', e.target.value)}
                  >
                    {BANNER_TYPES.map((bt) => (
                      <MenuItem key={bt.value} value={bt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: bt.color }} />
                          {bt.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isCloseable}
                      onChange={(e) => handleFormChange('isCloseable', e.target.checked)}
                    />
                  }
                  label="Users can dismiss this banner"
                />
              </Box>
            </Box>

            {/* Targeting Section */}
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1, mb: 1.5, display: 'block' }}>
                Targeting
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Target Team</InputLabel>
                  <Select
                    value={form.targetTeam}
                    label="Target Team"
                    onChange={(e) => handleFormChange('targetTeam', e.target.value)}
                  >
                    <MenuItem value="">All Teams</MenuItem>
                    {teams.map((team) => (
                      <MenuItem key={team.id} value={team.name}>{team.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Target Page</InputLabel>
                  <Select
                    value={form.targetPage}
                    label="Target Page"
                    onChange={(e) => handleFormChange('targetPage', e.target.value)}
                  >
                    {TARGET_PAGES.map((tp) => (
                      <MenuItem key={tp.value} value={tp.value}>{tp.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Action Button Section */}
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1, mb: 1.5, display: 'block' }}>
                Action Button (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Button Text (English)"
                  fullWidth
                  value={form.buttonTextEn}
                  onChange={(e) => handleFormChange('buttonTextEn', e.target.value)}
                />
                <TextField
                  label="Button Text (Spanish)"
                  fullWidth
                  value={form.buttonTextEs}
                  onChange={(e) => handleFormChange('buttonTextEs', e.target.value)}
                />
                <TextField
                  label="Button Link"
                  fullWidth
                  placeholder="/settings or https://..."
                  value={form.buttonLink}
                  onChange={(e) => handleFormChange('buttonLink', e.target.value)}
                  helperText="Internal path (e.g. /settings) or full URL (e.g. https://...)"
                />
              </Box>
            </Box>

            {/* Schedule Section */}
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1, mb: 1.5, display: 'block' }}>
                Schedule
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Start Date *"
                  type="datetime-local"
                  fullWidth
                  value={form.startDate}
                  onChange={(e) => handleFormChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date *"
                  type="datetime-local"
                  fullWidth
                  value={form.endDate}
                  onChange={(e) => handleFormChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            fullWidth={window.innerWidth < 600}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (selectedBanner ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Banner</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the banner "{selectedBanner?.titleEn}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BannerManagementPage;
